import { AlertTriangle, FileText, Globe, Puzzle } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { RecentPostsList } from './RecentPostsList';
import { formatRelativeTime } from '../../utils/format';
import type { WordPressSiteSummary } from '../../types';

function MiniStat({ icon: Icon, label, value }: { icon: typeof Globe; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-pulse-50 text-pulse-600 dark:bg-pulse-900/30 dark:text-pulse-300">
        <Icon size={14} />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{value}</p>
        <p className="text-[11px] text-slate-400">{label}</p>
      </div>
    </div>
  );
}

export function SiteSummaryCard({ site }: { site: WordPressSiteSummary }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-100">{site.label}</CardTitle>
        <Badge tone={site.connected ? 'success' : 'danger'}>{site.connected ? 'Connected' : 'Unreachable'}</Badge>
      </CardHeader>
      <CardBody>
        {!site.connected ? (
          <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{site.error ?? 'Could not reach this site.'}</span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-6">
              <MiniStat icon={Puzzle} label="Active plugins" value={`${site.plugins?.active ?? 0}/${site.plugins?.total ?? 0}`} />
              <MiniStat icon={FileText} label="Theme" value={site.activeTheme ?? 'Unknown'} />
            </div>
            <div className="mt-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Recently updated</p>
              <RecentPostsList posts={site.recentPosts ?? []} />
            </div>
          </>
        )}
        <p className="mt-3 text-[11px] text-slate-400">Checked {formatRelativeTime(site.fetchedAt)}</p>
      </CardBody>
    </Card>
  );
}
